import { RingMeter, RunStrip, Sparkline, StatStrip, StatTile, StateBar, formatInt, plural } from '@orgus/ui';
import type { RunFilter, WindowKey } from './model.ts';
import { WINDOWS } from './model.ts';
import type { AgentsGlance as Glance } from './glance.ts';

/**
 * Every tile's figure sits in the same slot: right of the number, one width, no taller than the
 * number's line, so the four numbers and the four captions share one baseline. A figure narrower
 * than the slot (the ring) sits at its centre, where the bars' and the sparkline's centres are.
 */
const FIGURE = 'w-16 sm:w-24';
const FIGURE_H = 24;

/**
 * The Agents glance strip: what runs now, how far the workflows are, how busy the window was,
 * and how the finished runs ended. Each caption says one fact, never one another tile says.
 * Running and Finished runs (to its failures) narrow the timeline below.
 */
export function AgentsGlance({ g, win, onFilter }: { g: Glance; win: WindowKey; onFilter: (f: RunFilter) => void }) {
  const finished = g.done + g.stopped;
  return (
    <StatStrip label="Agent counts" className="lg:grid-cols-4">
      {/* Subagents and workflow agents, not sessions: the live room below lists working sessions
          too, so a tile called "Running now" read 0 beside 4 working sessions (QA-3). */}
      <StatTile
        label="Subagents running"
        value={g.running}
        quietAtZero
        sub={g.running ? `In ${plural(g.sessionsRunning, 'session')}` : 'None running'}
        trend={g.running ? <StateBar className={FIGURE} counts={{ failed: g.failed, running: g.running, stale: finished }} label={`${formatInt(g.running)} running, ${formatInt(finished)} done, ${formatInt(g.failed)} failed`} /> : undefined}
        onOpen={() => onFilter('running')}
      />
      <StatTile
        label="Workflows running"
        featured
        value={g.workflowsRunning}
        quietAtZero
        sub={g.workflowsRunning ? `${formatInt(g.phasesDone)} of ${plural(g.phases, 'phase')} done` : 'None running'}
        // A progress ring only while a workflow runs: an empty ring reads as broken, not as idle.
        trend={
          g.workflowsRunning ? (
            <span className={`${FIGURE} flex justify-center`}>
              <RingMeter value={g.phases ? g.phasesDone / g.phases : 0} size={FIGURE_H} tone={1} label={`${formatInt(g.phasesDone)} of ${plural(g.phases, 'phase')} done`} />
            </span>
          ) : undefined
        }
      />
      <StatTile
        label="Runs"
        value={g.total}
        quietAtZero
        sub={`In ${WINDOWS[win].long}`}
        trend={
          g.total ? (
            <span className={`${FIGURE} flex`}>
              <Sparkline values={g.starts} width={96} height={FIGURE_H} min={0} slot={1} className="w-full" label={`Runs started in ${WINDOWS[win].long}: ${formatInt(g.starts.reduce((a, n) => a + n, 0))}`} />
            </span>
          ) : undefined
        }
        onOpen={() => onFilter('all')}
      />
      {/* How the finished runs ended: the strip is every outcome, so the title is what it shows. */}
      <StatTile
        label="Finished runs"
        value={finished + g.failed}
        quietAtZero
        sub={g.failed ? `${formatInt(g.failed)} failed` : 'None failed'}
        trend={finished + g.failed ? <RunStrip className={FIGURE} runs={g.outcomes.filter((o) => o !== 'running')} slots={24} label={`Finished runs: ${formatInt(finished)} done, ${formatInt(g.failed)} failed`} /> : undefined}
        onOpen={() => onFilter(g.failed ? 'failed' : 'all')}
      />
    </StatStrip>
  );
}
